import { memo } from "react";
import { MarkdownWithLaTeX } from "@/components/math/MarkdownWithLaTeX";
import SortableTable from "@/components/tables/SortableTable";
import ImageGroup from "@/components/chat/ImageGroup";
import { ContentBlock, isImageGroupBlock, isTextBlock } from "@shared/types/content-block";
import { useExtractTableFromLlmMessage } from "@/hooks/use-extract-table-from-llm-message";
import { useExtractMarkdownComponents } from "@/hooks/use-extract-markdown-components-from-llm-message";

interface ContentBlockRendererProps {
    contentBlocks: ContentBlock[];
    role: "user" | "assistant";
}

/**
 * Renders an array of ContentBlocks with proper formatting.
 * Handles text blocks (with markdown/tables) and image group blocks.
 */
export const ContentBlockRenderer = memo(({ contentBlocks, role }: ContentBlockRendererProps) => {
    return (
        <>
            {contentBlocks.map((block, blockIndex) => {
                if (isTextBlock(block)) {
                    return (
                        <TextBlockItem
                            key={`text-block-${blockIndex}`}
                            markdown={block.markdown}
                            role={role}
                            blockIndex={blockIndex}
                        />
                    );
                }
                
                if (isImageGroupBlock(block)) {
                    // Handle edge case: no images and no queries
                    const hasImages = block.images && block.images.length > 0;
                    const hasQueries = block.queries && block.queries.length > 0;
                    
                    if (!hasImages && !hasQueries) {
                        return null;
                    }

                    return (
                        <ImageGroup
                            key={`image-group-${blockIndex}`}
                            images={block.images}
                            queries={block.queries}
                            layout={block.layout}
                            caption={block.caption}
                        />
                    );
                }

                return null;
            })}
        </>
    );
});

ContentBlockRenderer.displayName = "ContentBlockRenderer";

/**
 * Internal component for rendering text blocks with table extraction.
 * Uses hooks internally for proper table/markdown parsing.
 */
interface TextBlockItemProps {
    markdown: string;
    role: "user" | "assistant";
    blockIndex: number;
}

const TextBlockItem = memo(({ markdown, role, blockIndex }: TextBlockItemProps) => {
    const { textParts, tables } = useExtractTableFromLlmMessage(markdown);
    const markdownComponents = useExtractMarkdownComponents(role);

    // Combine and sort text parts and tables by index
    const allParts = [
        ...textParts.map((part) => ({ ...part, partType: "text" as const })),
        ...tables.map((table) => ({ ...table, partType: "table" as const })),
    ].sort((a, b) => a.index - b.index);

    return (
        <>
            {allParts.map((part, i) => {
                if (part.partType === "text") {
                    return (
                        <MarkdownWithLaTeX
                            key={`block-${blockIndex}-text-${part.index}`}
                            components={markdownComponents as any}
                        >
                            {part.text}
                        </MarkdownWithLaTeX>
                    );
                }
                
                return (
                    <SortableTable
                        key={`block-${blockIndex}-table-${part.index}`}
                        headers={part.headers}
                        rows={part.rows}
                        tableIndex={i}
                    />
                );
            })}
        </>
    );
});

TextBlockItem.displayName = "TextBlockItem";
